<template>
	<v-dialog v-model="isActive" max-width="1300" scrollable persistent :retain-focus="false">
		<v-card>
			<v-toolbar flat dark color="primary">
				<v-toolbar-title>{{ modalTitle }}</v-toolbar-title>
			</v-toolbar>

			<template v-if="!ticket.id">
				<v-card-text class="pt-6 px-6">
					<v-layout wrap>
						<v-flex class="xs12 sm12">
							<v-layout wrap>
								<v-flex class="xs12 sm12 px-sm-2">
									<label>{{settings?.title}}</label>
								</v-flex>
								<v-flex class="xs12 sm12 px-sm-2" v-html="settings?.content"></v-flex>
								<v-flex class="xs12 sm3 px-sm-2">
									<v-text-field
										label="Submitter Name"
										v-model="ticket.submitter_name"
										:readonly="isLoading"
										:error-messages="messageFieldError('submitter_name')"
										density="compact"
                                        :disabled="isFilledSubmitterName"
									></v-text-field>
								</v-flex>

								<v-flex class="xs12 sm3 px-sm-2">
									<v-text-field
										label="Contact Email"
										v-model="ticket.submitter_email"
										:readonly="isLoading"
										:error-messages="messageFieldError('submitter_email')"
										density="compact"
                                        :disabled="isFilledSubmitterEmail"
									></v-text-field>
								</v-flex>

								<v-flex class="xs12 sm3 px-sm-2">
									<v-select
										v-model="ticket.category_id"
										:items="getOptionsWithEmptySlot(categories, ticket.category_id, 'name', 'id')"
										label="Category"
										item-value="id"
										item-text="name"
										:error-messages="messageFieldError('category_id')"
										density="compact"
									></v-select>
								</v-flex>

								<v-flex class="xs12 sm3 px-sm-2">
									<v-select
										v-model="ticket.priority_id"
										:items="getOptionsWithEmptySlot(priorities, ticket.priority_id, 'name', 'id')"
										label="Priority"
										item-value="id"
										item-text="name"
										:error-messages="messageFieldError('priority_id')"
										density="compact"
									></v-select>
								</v-flex>

								<v-flex class="xs12 sm3 px-sm-2">
									<v-text-field
										label="Subject"
										v-model="ticket.subject"
										:readonly="isLoading"
										:error-messages="messageFieldError('subject')"
										density="compact"
									></v-text-field>
								</v-flex>
							</v-layout>
						</v-flex>

						<v-flex class="xs12 sm12 mt-4">
							<EditorComponent :value.sync="ticket.comment.value" :error-messages="messageFieldError('value')" height="200px"/>
						</v-flex>

						<FileUploader :attachment.sync="ticket.comment.attachment" :fieldName="'comment_attachment'" />
					</v-layout>
				</v-card-text>

				<v-divider></v-divider>

				<v-card-actions class="justify-end pa-2">
					<v-alert
						v-if="successNotification.isActive"
						class="mr-6"
						density="compact"
						text
						type="success"
					>
						{{ successNotification.message }}
					</v-alert>

					<v-spacer></v-spacer>
					<v-btn color="green" :disabled="isLoading" text @click="submit">Submit</v-btn>
					<v-btn color="red" text @click="close">Cancel</v-btn>
				</v-card-actions>
			</template>

			<template v-if="ticket.id">
				<v-card-text class="px-6">
					<v-layout wrap>
						<v-flex xs12 sm3 fill-height class="left-panel pt-6">
                            <p v-if="isCentralSite && ticket.system"><strong>Current System: </strong>
                                <span class="text-red darken-2">{{ ticket.system }}</span>
                            </p>
                            <p v-if="isCentralSite && ticket.client_name"><strong>Client Name:</strong> {{ ticket.client_name }}</p>
							<p><strong>Contact Name:</strong> {{ ticket.submitter_name }}</p>
							<p><strong>Contact Email:</strong> {{ ticket.submitter_email }}</p>
							<p><strong>Subject:</strong> {{ ticket.subject }}</p>

							<v-flex class="xs12 sm12">
								<v-select
                                    v-if="isFullAccess"
									v-model="ticket.category_id"
									:items="getOptionsWithEmptySlot(categories, ticket.category_id, 'name', 'id')"
									label="Category"
									item-value="id"
									item-text="name"
									:error-messages="messageFieldError('category_id')"
									density="compact"
									@change="changeTicket"
								></v-select>

                                <p v-else-if="ticket.category"><strong>Category:</strong> {{ ticket.category }}</p>
                                <p v-else><strong>Category:</strong> Not specified</p>
							</v-flex>

							<v-flex class="xs12 sm12">
								<v-select
                                    v-if="isFullAccess"
									v-model="ticket.priority_id"
									:items="getOptionsWithEmptySlot(priorities, ticket.priority_id, 'name', 'id')"
									label="Priority"
									item-value="id"
									item-text="name"
									:error-messages="messageFieldError('priority_id')"
									density="compact"
									@change="changeTicket"
								></v-select>

                                <p v-else-if="ticket.priority"><strong>Priority:</strong> {{ ticket.priority }}</p>
                                <p v-else><strong>Priority:</strong> Not specified</p>
							</v-flex>

							<v-flex class="xs12 sm12">
								<v-select
                                    v-if="isFullAccess"
									v-model="ticket.status_id"
									:items="getOptionsWithEmptySlot(statuses, ticket.status_id, 'name', 'id')"
									label="Status"
									item-value="id"
									item-text="name"
									:error-messages="messageFieldError('status_id')"
									density="compact"
									@change="changeTicket"
								></v-select>

                                <p v-else-if="ticket.status"><strong>Status:</strong> {{ ticket.status }}</p>
                                <p v-else><strong>Status:</strong> Not specified</p>
							</v-flex>

							<v-flex class="xs12 sm12">
								<v-select
                                    v-if="isFullAccess"
									v-model="ticket.assigned_to_id"
									:items="getOptionsWithEmptySlot(supportAgentsForSelect, ticket.assigned_to_id, 'display_name', 'id')"
									label="Assigned To"
									item-value="id"
									item-text="display_name"
									density="compact"
									@change="changeTicket"
								></v-select>

                                <p v-else-if="ticket.assigned_to"><strong>Assigned To:</strong> {{ ticket.assigned_to }}</p>
                                <p v-else><strong>Assigned To:</strong> Not specified</p>
							</v-flex>

							<v-flex class="xs12 sm12" v-if="isFullAccess">
								<v-menu
									ref="menu_due_date"
									v-model="dueDateMenu"
									:close-on-content-click="false"
									transition="scale-transition"
									offset-y
									min-width="auto"
								>
									<template v-slot:activator="{ on, attrs }">
										<v-text-field
											ref="due_date"
											:value="formatDate(ticket.due_date, 'YYYY-MM-DD')"
											label="Due Date"
											prepend-inner-icon="fa-solid fa-calendar-days"
											readonly
											v-bind="attrs"
											v-on="on"
											:error-messages="messageFieldError('due_date')"
											clearable
										></v-text-field>
									</template>
									<v-date-picker
										v-model="ticket.due_date"
										:min="getToday()"
										@change="$refs.menu_due_date.save(ticket.due_date); changeTicket();"
									></v-date-picker>
								</v-menu>
							</v-flex>

                            <v-flex class="xs12 sm12" v-if="!isFullAccess">
                                <p v-if="ticket.due_date"><strong>Due Date:</strong> {{ ticket.due_date }}</p>
                                <p v-else><strong>Due Date:</strong> Not specified</p>
                            </v-flex>

						</v-flex>

						<v-flex xs12 sm9 fill-height class="right-panel">
							<v-flex class="d-flex flex-wrap gap-2 pt-6">
								<v-btn
									v-if="(!add_private_comment && !add_public_comment) && isFullAccess"
									class="text-uppercase mr-2 mb-3"
									outlined dense
									@click="addPrivateComment()">
									Add private note
								</v-btn>

								<v-btn
									v-if="!add_private_comment && !add_public_comment"
									class="text-uppercase mb-3"
									outlined dense
									@click="addPublicComment()">
									Add reply message
								</v-btn>

								<v-btn
									v-if="add_private_comment"
									class="text-uppercase mb-3"
									outlined dense
									@click="cancelPrivateComment()">
									Cancel private note
								</v-btn>

								<v-btn
									v-if="add_public_comment"
									class="text-uppercase mb-3"
									outlined dense
									@click="cancelPublicComment()">
									Cancel reply message
								</v-btn>
							</v-flex>

							<v-flex v-if="add_public_comment" wrap>
								<v-flex class="xs12 sm12 mb-6 mt-6">
									<span class="text-h6">Reply Message</span>
									<EditorComponent :value.sync="ticket.comment.value" :error-messages="messageFieldError('value')" height="200px"/>
								</v-flex>
								<FileUploader :attachment.sync="ticket.comment.attachment" :fieldName="'attachment'"/>

								<v-flex class="xs12 sm10 mb-6"></v-flex>
								<v-flex class="xs12 sm2 mb-6">
									<v-btn block color="primary" :disabled="isLoading" @click="submit">Submit</v-btn>
								</v-flex>
							</v-flex>

							<v-flex v-if="add_private_comment" wrap>
								<v-flex class="xs12 sm12 mb-6 mt-6">
									<span class="text-h6">Private Note</span>
									<EditorComponent :value.sync="ticket.comment.value" :error-messages="messageFieldError('value')" height="200px"/>
								</v-flex>
								<FileUploader :attachment.sync="ticket.comment.attachment" :fieldName="'attachment'"/>

								<v-flex class="xs12 sm10 mb-6"></v-flex>
								<v-flex class="xs12 sm2 mb-6">
									<v-btn block color="primary" :disabled="isLoading" @click="submit">Submit</v-btn>
								</v-flex>
							</v-flex>

							<v-flex xs12 v-for="comment in ticket.comments" :key="comment.id + comment.visibility">
                                <v-card
                                    outlined
                                    elevation="2"
                                    class="mb-4 px-4 py-3"
                                    :class="comment.visibility === 'private' ? 'private-comment' : ''"
                                >
                                    <v-layout align-center justify-space-between class="mb-2">
                                        <v-flex xs6 class="d-flex align-center text-caption text-grey-darken-1">
                                            {{ comment.created_at }}
                                            <div v-if="comment.visibility === 'private'" class="d-flex align-center ml-2 private-comment__text">
                                                <v-icon small color="primary" class="mr-1">fa-solid fa-lock</v-icon>
                                                <span>Private</span>
                                            </div>
                                        </v-flex>
                                        <v-flex xs6 class="text-right font-weight-medium text-primary">
                                            {{ comment.author_name }}
                                        </v-flex>
                                    </v-layout>

									<v-layout>
										<v-flex xs12>
											<v-sheet class="pa-3 rounded-lg mt-2 text-body-1 text-break"
													 color="grey-lighten-3">
												<span v-html="comment.comment"></span>
											</v-sheet>
										</v-flex>
									</v-layout>

                                    <AttachmentsBlock :attachments="comment.attachments" />

                                    <v-layout v-if="comment.visibility !== 'private' && comment.read_at" class="mb-2">
										<v-flex xs12 class="text-caption text-grey-darken-1 mt-4">
											Read at: {{ comment.read_at }}
										</v-flex>
									</v-layout>
								</v-card>
							</v-flex>

							<v-card outlined elevation="2" class="mb-4 px-4 py-3">
								<p class="text-h6 text-red">History</p>
								<v-flex v-if="ticket.history && ticket.history.length > 0" xs12
										class="text-caption text-grey-darken-1" v-html="ticket.history">
								</v-flex>

								<v-flex v-else xs12 class="text-caption text-grey-darken-1">
									No history available
								</v-flex>
							</v-card>
						</v-flex>
					</v-layout>
				</v-card-text>

				<v-divider></v-divider>

				<v-card-actions class="justify-end pa-2">
					<v-alert
						v-if="successNotification.isActive"
						class="mr-6"
						density="compact"
						text
						type="success"
					>
						{{ successNotification.massage }}
					</v-alert>
                    <v-btn color="primary" text @click="close">Close</v-btn>
				</v-card-actions>
			</template>
		</v-card>
        <UploadProgressBar
            :active="uploadActive"
            :progress="uploadProgress"
            :file-size="uploadFileSize"
            :files-count="uploadFilesCount"
        />
	</v-dialog>
</template>

<script>
import notifications from "../../mixins/notifications";
import staticData from "../../mixins/staticData";
import EditorComponent from "../../components/EditorComponent.vue";
import maxFileSizeMixin from "../../mixins/files/maxFileSize";
import FileUploader from "../components/FileUploader.vue";
import AttachmentsBlock from "../../components/AttachmentsBlock.vue";
import UploadProgressBar from "../../components/UploadProgressBar.vue";

export default {
	name: "TicketModal",
	components: {UploadProgressBar, AttachmentsBlock, FileUploader, EditorComponent},
	mixins: [
		notifications,
		staticData,
		maxFileSizeMixin
	],
	props: {
		isActive: {
			type: Boolean,
			default: false
		},
		id: {
			type: Number,
			default: 0
		},
        isCentralSite: {
            type: Boolean,
            default: false
        },
        isFullAccess: {
            type: Boolean,
            default: false
        },
		url: {
			type: String,
			required: true,
		},
		title: {
			type: String,
			default: 'Ticket'
		},
		settings: {
			type: Object,
			default: {}
		},
		categories: {
			type: Array,
			default: []
		},
		priorities: {
			type: Array,
			default: []
		},
		statuses: {
			type: Array,
			default: []
		},
		support_agents: {
			type: Array,
			default: []
		},
	},
	data() {
		return {
			dueDateMenu: false,
			isLoading: false,
            isFilledSubmitterName: false,
            isFilledSubmitterEmail: false,
			ticket: {
				id: 0,
				submitter_name: '',
				submitter_email: '',
				category_id: null,
				priority_id: null,
				status_id: null,
				assigned_to_id: null,
				due_date: null,
				subject: '',
				history: '',
				comment: {
					value: '',
					visibility: 'public',
					attachment: null
				},
				comments: [],
			},
			add_private_comment: false,
			add_public_comment: false,
			private: {
				comment: '',
				attachment: null
			},
			public: {
				comment: '',
				attachment: null
			},
            uploadActive: false,
            uploadProgress: 0,
            uploadFileSize: 0,
            uploadFilesCount: 0
		}
	},
	computed: {
		modalTitle() {
			return this.id === 0 ? 'New ' + this.title : 'Edit ' + this.title;
		},
        supportAgentsForSelect() {
            const items = [...this.support_agents];

            if (!this.ticket.assigned_to_id || items.some(item => item.id === this.ticket.assigned_to_id)) {
                return items;
            }

            items.push({id: this.ticket.assigned_to_id, display_name: this.ticket.assigned_to});

            return items;
        },
	},
	watch: {
		'$props.isActive': function (value) {
            this.loadAuth();
            this.clearValidateErrors();
			if (value) {
				if (this.id > 0) {
					this.loadData();
				}
			} else {
				this.clearData();
			}
		},
	},
	methods: {
        loadAuth() {
            this.isLoading = true;

            this.$api.get('/v1/auth').then(response => {
                if (response.data?.is_auth) {
                    const { name, email } = response.data.user;

                    this.ticket.submitter_name = name;
                    this.ticket.submitter_email = email;

                    this.isFilledSubmitterName = Boolean(name);
                    this.isFilledSubmitterEmail = Boolean(email);
                }
            }).finally(() => {
                this.isLoading = false;
            });
        },
		loadData() {
			this.isLoading = true;

			this.$api.get(this.url + '/' + this.id).then(response => {
				if (response.data) {
					this.ticket = response.data;
					this.clearComment();
				}
			}).finally(() => {
				this.isLoading = false;
			});
		},
		clearData() {
			this.ticket.id = 0;
			this.ticket.submitter_name = '';
			this.ticket.submitter_email = '';
			this.ticket.category_id = null;
			this.ticket.priority_id = null;
			this.ticket.subject = '';
			this.ticket.status_id = null;
			this.ticket.assigned_to_id = null;
			this.ticket.due_date = null;
			this.ticket.history = '';
			this.clearComment();
		},
		submit() {
            this.isLoading = true;

			if (this.ticket.id > 0) {
				this.submitComment();
			} else {
				this.createTicket();
			}
		},
        submitComment() {
            const formData = new FormData();

            for (const key in this.ticket.comment) {
                if (this.ticket.comment[key] !== null && this.ticket.comment[key] !== undefined) {
                    formData.append(key, this.ticket.comment[key]);
                }
            }

            this.uploadActive = true;
            this.uploadProgress = 0;
            this.uploadFileSize = 0;
            this.uploadFilesCount = 0;

            if (this.ticket.comment.attachment instanceof File) {
                this.uploadFileSize = this.ticket.comment.attachment.size;
                this.uploadFilesCount = 1;
            }

            this.$api.post(
                this.url + '/' + this.ticket.id + '/comments',
                formData,
                {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (e) => {
                        if (!e.total) return;
                        this.uploadProgress = Math.round((e.loaded * 100) / e.total);
                    }
                }
            )
                .then(response => {
                    if (response.data) {
                        this.ticket = response.data;
                        this.clearComment();
                        this.clearValidateErrors();
                        this.cancelPrivateComment();
                        this.cancelPublicComment();
                        this.showSuccessNotification('Message sent');
                    }
                })
                .catch((error) => {
                    if (error.response && error.response.data.errors) {
                        this.setValidateErrors(error.response.data.errors);
                    }
                })
                .finally(() => {
                    this.isLoading = false;
                    this.uploadActive = false;
                    this.uploadProgress = 0;
                    this.$emit('reload');
                });
        },
        createTicket() {
            const formData = new FormData();

            this.uploadActive = true;
            this.uploadProgress = 0;
            this.uploadFileSize = 0;
            this.uploadFilesCount = 0;

            for (const key in this.ticket) {
                if (key === 'comment' && this.ticket.comment) {

                    if (this.ticket.comment.value) {
                        formData.append('value', this.ticket.comment.value);
                    }

                    if (this.ticket.comment.attachment instanceof File) {
                        formData.append('attachment', this.ticket.comment.attachment);

                        this.uploadFileSize += this.ticket.comment.attachment.size;
                        this.uploadFilesCount++;
                    }

                } else {
                    if (this.ticket[key] !== null && this.ticket[key] !== undefined) {
                        formData.append(key, this.ticket[key]);
                    }
                }
            }

            this.$api.post(
                this.url,
                formData,
                {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (e) => {
                        if (!e.total) return;
                        this.uploadProgress = Math.round((e.loaded * 100) / e.total);
                    }
                }
            )
                .then(response => {
                    if (response.data) {
                        this.showSuccessNotification('Data saved');
                        this.clearValidateErrors();
                        this.close();
                    }
                })
                .catch((error) => {
                    if (error.response && error.response.data.errors) {
                        this.setValidateErrors(error.response.data.errors);
                    }
                })
                .finally(() => {
                    this.isLoading = false;
                    this.uploadActive = false;
                    this.uploadProgress = 0;
                    this.$emit('reload');
                });
        },
        changeTicket() {
            this.isLoading = true;

            const payload = {
                subject: this.ticket.subject,
                status_id: this.ticket.status_id,
                assigned_to_id: this.ticket.assigned_to_id,
                category_id: this.ticket.category_id,
                priority_id: this.ticket.priority_id,
                due_date: this.ticket.due_date,
            };

            const formData = new FormData();
            formData.append('_method', 'PUT');

            Object.entries(payload).forEach(([key, value]) => {
                if (value !== null && value !== undefined) {
                    formData.append(key, value);
                }
            });

            this.uploadActive = true;
            this.uploadProgress = 0;
            this.uploadFileSize = 0;
            this.uploadFilesCount = 0;

            this.$api.post(this.url + '/' + this.ticket.id, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (e) => {
                    if (!e.total) return;
                    this.uploadProgress = Math.round((e.loaded * 100) / e.total);
                }
            })
                .then(response => {
                    if (response.data) {
                        this.ticket = response.data;
                        this.clearComment();

                        this.showSuccessNotification('Data updated');
                        this.clearValidateErrors();
                    }
                })
                .catch((error) => {
                    if (error.response && error.response.data.errors) {
                        this.setValidateErrors(error.response.data.errors);
                    }
                })
                .finally(() => {
                    this.$emit('reload');
                    this.isLoading = false;
                    this.uploadActive = false;
                    this.uploadProgress = 0;
                });
        },
		close() {
			this.isLoading = false;
			this.$emit('update:isActive', false);
            this.cancelPublicComment();
            this.cancelPrivateComment();

			this.$nextTick(() => {
				this.itemEdit = {id: null, isActive: false};
			});
		},
		clearComment() {
			this.ticket.comment = {
				value: '',
				visibility: 'public',
				attachment: null
			};
		},
		addPrivateComment() {
			this.add_private_comment = true;
			this.ticket.comment.visibility = 'private';
		},
		addPublicComment() {
			this.add_public_comment = true;
			this.ticket.comment.visibility = 'public';
		},
		cancelPrivateComment() {
			this.add_private_comment = false;
			this.clearComment();
		},
		cancelPublicComment() {
			this.add_public_comment = false;
			this.clearComment();
		},
		formatDate(date, format = 'YYYY-MM-DD') {
			if (!date) return '';
			return new Date(date).toISOString().split('T')[0];
		},
		getToday() {
			return new Date().toISOString().substr(0, 10);
		},

	}
}
</script>

<style scoped>
.left-panel {
	padding: 16px;
	overflow-y: auto;
	height: 100%;
	position: sticky;
	top: 0;
}

.right-panel {
	padding: 0 16px 16px 16px;
	overflow-y: auto;
	border-left: 1px solid #ddd;
	height: 100%;
	min-height: 100vh;
}

.v-dialog > .v-card > .v-card__subtitle, .v-dialog > .v-card > .v-card__text {
	padding: 0;
}

.theme--light.v-divider {
	margin-top: 0;
}

.private-comment {
    border-left: 5px solid #f44336 ;
}
</style>
